import { useState } from "react";
import {
  MapPin,
  Phone,
  Clock,
  Mail,
  MessageSquare,
  ExternalLink,
} from "lucide-react";
import { FadeIn } from "./FadeIn";
import {
  PHONE_DISPLAY,
  PHONE_TEL,
  WHATSAPP_URL,
  EMAIL_DISPLAY,
  GOOGLE_MAPS_URL,
} from "../data/content";

export const Contact = () => {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim() && !phone.trim() && !message.trim()) {
      window.open(WHATSAPP_URL, "_blank");
      return;
    }
    const formattedText = `Hello G69 Spa Gurugram,\n\nName: ${name.trim() || "Guest"}\nPhone: ${phone.trim() || "Not provided"}\nMessage: ${message.trim() || "I would like to inquire about spa services."}`;
    const url = `https://wa.me/917005120041?text=${encodeURIComponent(formattedText)}`;
    window.open(url, "_blank");
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="px-4 sm:px-8 lg:px-12 py-16 md:py-24 max-w-7xl mx-auto"
    >
      <FadeIn className="flex flex-col gap-4 mb-14">
        <p className="text-amber-400 text-xs font-bold uppercase tracking-[0.3em]">
          Visit Us &amp; Enquire
        </p>
        <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter text-white">
          Contact &amp; Location
        </h2>
      </FadeIn>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left Side: G69 Gurugram Branch Card */}
        <FadeIn className="flex flex-col gap-6">
          <div className="rounded-3xl border-2 border-emerald-400/80 bg-gradient-to-b from-emerald-950/20 via-black to-[#080808] p-8 shadow-xl shadow-emerald-950/30 space-y-6">
            <h3 className="text-2xl sm:text-3xl font-serif font-bold text-emerald-400">
              G69 Spa | Gurugram
            </h3>

            <div className="space-y-4 text-sm sm:text-base">
              {/* Direct Call */}
              <a
                href={PHONE_TEL}
                className="flex items-center gap-3 text-white/90 hover:text-emerald-400 transition-colors font-medium"
              >
                <Phone className="w-5 h-5 text-emerald-400 shrink-0" />
                <span>{PHONE_DISPLAY}</span>
              </a>

              {/* WhatsApp */}
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 text-white/90 hover:text-emerald-400 transition-colors font-medium"
              >
                <MessageSquare className="w-5 h-5 text-emerald-400 shrink-0" />
                <span>{PHONE_DISPLAY} (WhatsApp)</span>
              </a>

              {/* Email */}
              <a
                href={`mailto:${EMAIL_DISPLAY}`}
                className="flex items-center gap-3 text-white/90 hover:text-emerald-400 transition-colors font-medium"
              >
                <Mail className="w-5 h-5 text-emerald-400 shrink-0" />
                <span>{EMAIL_DISPLAY}</span>
              </a>

              {/* Google Location */}
              <a
                href={GOOGLE_MAPS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-3 text-white/90 hover:text-emerald-400 transition-colors font-medium group"
              >
                <MapPin className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                <span className="underline decoration-emerald-400/50 underline-offset-4 group-hover:decoration-emerald-400">
                  Sikandarpur – Gurgaon (Google Location)
                </span>
                <ExternalLink className="w-4 h-4 text-emerald-400 shrink-0 mt-1 opacity-70 group-hover:opacity-100" />
              </a>

              {/* Operating Hours */}
              <div className="flex items-center gap-3 text-amber-300 font-bold pt-2 text-xs sm:text-sm">
                <Clock className="w-5 h-5 text-amber-400 shrink-0" />
                <span>Open 7 Days • 10:00 AM to 11:00 PM</span>
              </div>
            </div>

            <div className="pt-4 flex flex-col sm:flex-row gap-4">
              <a
                href={PHONE_TEL}
                data-testid="contact-call-btn"
                className="flex-1 text-center bg-gradient-to-r from-amber-300 via-yellow-500 to-amber-600 text-black px-6 py-3.5 rounded-full text-xs font-extrabold uppercase tracking-[0.2em] hover:shadow-[0_0_20px_rgba(245,158,11,0.4)] transition-all duration-300"
              >
                Call Branch
              </a>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-btn"
                className="flex-1 text-center border border-emerald-400/70 text-emerald-400 px-6 py-3.5 rounded-full text-xs font-extrabold uppercase tracking-[0.2em] hover:bg-emerald-400 hover:text-black transition-all duration-300"
              >
                WhatsApp Desk
              </a>
            </div>
          </div>
        </FadeIn>

        {/* Right Side: Message Form */}
        <FadeIn delay={0.15}>
          <div className="rounded-3xl border border-emerald-400/50 bg-[#0a0a0a] p-8 shadow-2xl space-y-6">
            <h3 className="text-xl font-bold text-white tracking-wide">
              Message
            </h3>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="text-xs uppercase tracking-wider text-white/60 block mb-1.5 font-bold">
                  Your Name
                </label>
                <input
                  type="text"
                  placeholder="Enter your full name..."
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full bg-black/90 border border-white/20 focus:border-emerald-400 px-4 py-3.5 rounded-xl text-sm text-white outline-none transition-all"
                />
              </div>

              <div>
                <label className="text-xs uppercase tracking-wider text-white/60 block mb-1.5 font-bold">
                  Phone Number
                </label>
                <input
                  type="tel"
                  placeholder="Enter your phone number..."
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full bg-black/90 border border-white/20 focus:border-emerald-400 px-4 py-3.5 rounded-xl text-sm text-white outline-none transition-all"
                />
              </div>

              <div>
                <label className="text-xs uppercase tracking-wider text-white/60 block mb-1.5 font-bold">
                  Message
                </label>
                <textarea
                  rows="4"
                  placeholder="Write your message here..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="w-full bg-black/90 border border-white/20 focus:border-emerald-400 px-4 py-3.5 rounded-xl text-sm text-white outline-none resize-none transition-all"
                />
              </div>

              <button
                type="submit"
                className="w-full bg-gradient-to-r from-amber-400 via-amber-300 to-yellow-500 text-black font-black py-4 rounded-xl text-xs uppercase tracking-[0.2em] shadow-lg shadow-amber-950/40 hover:scale-[1.02] active:scale-[0.98] transition-all"
              >
                Send Message
              </button>
            </form>
          </div>
        </FadeIn>
      </div>
    </section>
  );
};
